import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const UAE_TIMEZONE = "Asia/Dubai";

function isValidTimezone(value: string | undefined): value is string {
  if (!value) {
    return false;
  }

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function getBrowserTimezone(): string {
  if (typeof window === "undefined") {
    return UAE_TIMEZONE;
  }

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (isValidTimezone(browserTimezone)) {
    return browserTimezone;
  }

  const guessedTimezone = dayjs.tz.guess();
  return isValidTimezone(guessedTimezone) ? guessedTimezone : UAE_TIMEZONE;
}

export function getDisplayTimezone(pathname?: string): string {
  const activePath =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  return activePath === "/admin" || activePath.startsWith("/admin/")
    ? UAE_TIMEZONE
    : getBrowserTimezone();
}

export function getTimezoneLabel(timezoneName = getDisplayTimezone()): string {
  const parts = timezoneName.split("/");
  return parts[parts.length - 1]?.replace(/_/g, " ") || timezoneName;
}

export function toDisplayDayjs(
  input?: string | Date | dayjs.Dayjs,
  pathname?: string,
): dayjs.Dayjs {
  return dayjs(input).tz(getDisplayTimezone(pathname));
}

/** Format a date/timestamp to a human-readable string in the active display timezone. */
export function formatDisplayTime(
  input: string | Date | dayjs.Dayjs,
  format = "DD MMM YYYY, hh:mm A",
): string {
  return toDisplayDayjs(input).format(format);
}

/** Get today's date string in the active display timezone. */
export function todayDisplayDate(): string {
  return toDisplayDayjs().format("YYYY-MM-DD");
}

/** Format duration in seconds to "Xh Ym" or "Xm" */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return "0m";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Format seconds to "X.Xh" */
export function formatHours(seconds: number): string {
  return `${(seconds / 3600).toFixed(1)}h`;
}
